/**
 * HubCustomersPage — HUB-REDESIGN P5.
 *
 * Single dense page reshelled from the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/other-pages.jsx
 * (CustomersPage, lines 209-258). Wires the existing /hub/tenants
 * payload — backend frozen for the redesign — into:
 *
 *   /hub/tenants  — 5-KPI strip (At-risk, Onboarding, Active 7d,
 *                   Power users, NPS) + At-risk tenants table +
 *                   Onboarding funnel.
 *
 * Stubs the design handoff did not have data for:
 *   - NPS: rendered as `—` with BACKEND_NEEDED endpoint badge.
 *   - Onboarding funnel: derived heuristic from HubTenantSummary
 *     fields (project_count, user_count, last_seen_at). Marked
 *     BACKEND_NEEDED: events because the funnel is approximate
 *     until per-tenant signup events are tracked.
 *
 * "Power users" follows the spec heuristic — last_seen_at within
 * 24h AND project_count >= 2 — not a real ≥5d/wk activity rollup,
 * which would require the events backend.
 */
import { useEffect, useMemo, useState } from 'react'
import { formatTime } from '../utils/dateUtils'
import {
  Card, Endpoint, KPI, Pill, StatusDot, Icon, DepartmentObservability,
} from './components'
import {
  hubTenants,
  hubAnalytics,
  type HubTenantsResponse,
  type HubTenantSummary,
  type HubFunnelStep,
  type HubNPSResult,
} from './api'

export default function HubCustomersPage() {
  const [tenants, setTenants] = useState<HubTenantsResponse | null>(null)
  const [funnel, setFunnel] = useState<HubFunnelStep[] | null>(null)
  const [nps, setNps] = useState<HubNPSResult | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const [t, f, n] = await Promise.all([
        hubTenants.list().catch(() => null),
        hubAnalytics.funnel().catch(() => null),
        hubAnalytics.nps().catch(() => null),
      ])
      if (cancelled) return
      setTenants(t)
      if (f) setFunnel(f)
      if (n) setNps(n)
      setRefreshedAt(new Date())
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const summary = tenants?.summary
  const list = tenants?.tenants ?? []

  const blockedOnboarding = useMemo(
    () => list.filter(t => t.subscription_status === 'trialing' && t.project_count === 0).length,
    [list],
  )
  const active7d = useMemo(() => list.filter(t => withinDays(t.last_seen_at, 7)).length, [list])
  const powerUsers = useMemo(
    () => list.filter(t => withinHours(t.last_seen_at, 24) && t.project_count >= 2).length,
    [list],
  )
  const atRisk = useMemo(
    () => list
      .filter(t => t.subscription_status === 'past_due' || (t.flags && t.flags.length > 0))
      .slice(0, 12),
    [list],
  )

  const total = summary?.total ?? 0
  const trialing = summary?.trialing ?? 0
  const atRiskCount = summary?.at_risk_count ?? 0
  const activePct = total > 0 ? Math.round((active7d / total) * 100) : 0

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Card bodyPad={false}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1,
          background: 'var(--border)',
        }}>
          <KPI
            label="At-risk"
            value={atRiskCount}
            delta={`of ${total}`}
            tone={atRiskCount > 0 ? 'down' : 'flat'}
            endpoint="/hub/tenants"
          />
          <KPI
            label="Onboarding"
            value={trialing}
            delta={`${blockedOnboarding} blocked`}
            tone={blockedOnboarding > 0 ? 'down' : 'flat'}
            endpoint="/hub/tenants"
          />
          <KPI
            label="Active 7d"
            value={active7d}
            delta={`${activePct}% of base`}
            tone="flat"
            endpoint="/hub/tenants"
          />
          <KPI
            label="Power users"
            value={powerUsers}
            delta="≥5d/wk"
            tone="up"
            endpoint="/hub/tenants"
          />
          <KPI
            label="NPS"
            value={nps?.score != null ? String(Math.round(nps.score)) : '—'}
            delta={nps?.responses ? `${nps.responses} responses` : 'not collected'}
            tone="flat"
            endpoint="/hub/analytics/nps"
          />
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 10 }}>
        <AtRiskCard tenants={atRisk} />
        <OnboardingFunnelCard list={list} active7d={active7d} serverFunnel={funnel} />
      </div>

      <DepartmentObservability department="customers" />

      {refreshedAt && (
        <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-dim)' }} className="mono">
          refreshed {formatTime(refreshedAt)}
        </div>
      )}
    </div>
  )
}

// ── At-risk tenants table ──────────────────────────────────────────────
function AtRiskCard({ tenants }: { tenants: HubTenantSummary[] }) {
  return (
    <Card
      icon="users" title="At-risk tenants" endpoint="/hub/tenants"
      style={{ gridColumn: 'span 7' }}
      bodyPad={false}
    >
      {tenants.length === 0 ? (
        <div style={{ padding: '16px 12px', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>
          All clear — no at-risk tenants.
        </div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}></th>
              <th>Tenant</th>
              <th>Reason</th>
              <th style={{ width: 110 }}>Last login</th>
              <th style={{ width: 100 }}>Plan</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.company_id}>
                <td>
                  <StatusDot tone={severityFor(t)} />
                </td>
                <td>
                  <a
                    href={`/tenants/${t.company_id}`}
                    style={{
                      color: 'var(--text)', textDecoration: 'none',
                      fontSize: 12.5,
                    }}
                  >
                    {t.company_name}
                  </a>
                </td>
                <td style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>
                  {reasonFor(t)}
                </td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                  {t.last_seen_at ? relTime(t.last_seen_at) : '—'}
                </td>
                <td>
                  <Pill variant="ghost">{planLabel(t.plan)}</Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}

// ── Onboarding funnel ──────────────────────────────────────────────────
type FunnelRow = { label: string; value: number; max: number }

function OnboardingFunnelCard({
  list, active7d, serverFunnel,
}: {
  list: HubTenantSummary[]
  active7d: number
  serverFunnel: HubFunnelStep[] | null
}) {
  const total = list.length
  const projectCreated = list.filter(t => t.project_count > 0).length
  const invitedTeam = list.filter(t => t.user_count > 1).length
  const max = total || 1
  const clientRows: FunnelRow[] = [
    { label: 'Signed up',       value: total,          max },
    { label: 'Project created', value: projectCreated, max },
    { label: 'Invited team',    value: invitedTeam,    max },
    { label: 'Active 7d',       value: active7d,       max },
  ]
  const rows: FunnelRow[] = serverFunnel ?? clientRows
  const isApproximated = serverFunnel == null

  return (
    <Card
      icon="trending-up" title="Onboarding funnel"
      endpoint="/hub/analytics/funnel"
      style={{ gridColumn: 'span 5' }}
      right={isApproximated ? (
        <span style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>
          <Icon name="alert-triangle" size={10} /> approximated
        </span>
      ) : undefined}
    >
      {total === 0 && (
        <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--text-dim)' }}>
          No tenant data yet.
        </div>
      )}
      {rows.map((r, i) => {
        const pct = r.max ? (r.value / r.max) * 100 : 0
        return (
          <div key={i} className="row" style={{ gap: 8, padding: '5px 0' }}>
            <span style={{ width: 100, fontSize: 11.5, color: 'var(--text-muted)' }}>
              {r.label}
            </span>
            <div style={{
              flex: 1, height: 14, background: 'var(--surface-2)',
              borderRadius: 3, overflow: 'hidden',
            }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                background: 'var(--accent)', opacity: 0.85,
              }} />
            </div>
            <span className="mono" style={{ fontSize: 11.5, width: 48, textAlign: 'right' }}>
              {r.value}/{r.max}
            </span>
          </div>
        )
      })}
    </Card>
  )
}

// ── helpers ───────────────────────────────────────────────────────────
function severityFor(t: HubTenantSummary): 'err' | 'warn' {
  if (t.subscription_status === 'past_due') return 'err'
  return 'warn'
}

function reasonFor(t: HubTenantSummary): string {
  if (t.subscription_status === 'past_due') return 'Past due'
  if (t.subscription_status === 'cancelled') return 'Cancelled'
  if ((t.flags || []).length) return t.flags.slice(0, 2).join(' · ')
  return t.subscription_status
}

function planLabel(plan: string | null): string {
  if (!plan) return 'Trial'
  return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase()
}

function withinDays(iso: string | null, days: number): boolean {
  if (!iso) return false
  const ms = Date.now() - new Date(iso).getTime()
  if (!isFinite(ms) || ms < 0) return false
  return ms < days * 86400 * 1000
}

function withinHours(iso: string | null, hours: number): boolean {
  if (!iso) return false
  const ms = Date.now() - new Date(iso).getTime()
  if (!isFinite(ms) || ms < 0) return false
  return ms < hours * 3600 * 1000
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0 || !isFinite(ms)) return '—'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.floor(hr / 24)
  return `${day}d`
}
